import L from 'leaflet';
import 'leaflet-draw';
import { toString, map } from 'lodash';

import * as layerOps from '../../api/Layers/layer-operations';
import * as styleOps from '../../api/Styles/style-operations';

import adaptURL from '../adapt-url';
import { getGeoJSONStyle } from '../geojson-layer-style';
import getGeoJSONFromCSV from '../get-geojson-from-csv';
import getGeoJSONFromExcel from '../get-geojson-from-excel';
import joinGeoJSONThematicLayer from '../join-geojson-thematic-layers';
import loadGeoJSONFromURL from '../load-geojson-from-url';
import loadStyleFromURL from '../load-style-from-url';
import methodPromise from '../method-promise-wrapper';

/**
 * Builds the GeoJSON layer.
 *
 * @async
 * @param {object} arg - The object argument.
 * @param {object} arg.layer - The layer object.
 * @param {object} arg.style - The style object.
 * @param {boolean} [arg.isPartOfMap=false] - Flag that is true if layer is part of a map.
 * @param {number} [arg.zIndex=400] - The zIndex for the layer.
 * @returns {Promise<object>} The leaflet layer to add to the map with the geojson and the style.
 */
const buildGeoJSONLayer = async ({
  isPartOfMap = false,
  layer,
  popupProp,
  style,
  zIndex = 400
}) => {
  let geojsonFile = '';
  let layerToDraw = '';
  let styleOptions;
  let options;
  let customStyleObject;
  const url = adaptURL(layerOps.getURL(layer));
  try {
    switch (true) {
      case layerOps.isAnnotationsLayer(layer):
        geojsonFile = layerOps.getAnnotationLayerProperties(layer).geojsonAnnotations;
        break;
      case layerOps.isCSV(layer):
        geojsonFile = await getGeoJSONFromCSV(layer);
        break;
      case layerOps.isExcel(layer):
      case layerOps.isGoogleSheets(layer):
        geojsonFile = await getGeoJSONFromExcel(layer);
        break;
      case layerOps.isFiware(layer):
        geojsonFile = await methodPromise('layers.loadFiware', {
          fiwareLayerName: layerOps.getFiwareLayerName(layer),
          georefType: layerOps.getGeorefType(layer),
          loadParameters: {
            ...layerOps.getFiwareParams(layer),
            fiwareTokenURL: layerOps.getURL(layer)
          },
          spatialTableFields: layerOps.getTableSpatialObject(layer),
          locationIndex: layerOps.getLocationIndex(layer),
          addressIndex: layerOps.getAddressIndex(layer)
        });
        break;
      case layerOps.isGeoJSON(layer):
        geojsonFile = await loadGeoJSONFromURL(url);
        break;
      case layerOps.isJoin(layer):
        geojsonFile = await joinGeoJSONThematicLayer({
          geojsonId: layerOps.getGeoJSONId(layer),
          thematicId: layerOps.getThematicId(layer),
          geojsonPropToJoin: layerOps.getGeoJSONPropToJoin(layer),
          thematicKeyToJoin: layerOps.getThematicKeyToJoin(layer),
          sorensenDiceMinimun: layerOps.getSorensenDiceMinimun(layer),
          layerSubType: layerOps.getSubType(layer)
        });
        break;
      case layerOps.isOGR(layer):
        geojsonFile = await methodPromise('layers.loadOGRGeoJSON', {
          url: adaptURL(layerOps.getURL(layer)),
          layerName: layerOps.getOGRLayerName(layer),
          attribute: layerOps.getOGRAttributes(layer).split(',')
        });
        break;
      case layerOps.isPostgreSQL(layer):
        geojsonFile = await methodPromise('layers.loadPostgreSQL', {
          loadParameters: layerOps.getConnectionParams(layer),
          attributes: map(layerOps.getGeoJSONProps(layer), 'name')
        });
        break;
      default:
        break;
    }

    if (styleOps.isCustom(style))
      customStyleObject = await loadStyleFromURL({
        url: adaptURL(styleOps.getStyleURL(style)),
        layer
      });

    if (isPartOfMap) {
      styleOptions = await getGeoJSONStyle({
        style,
        layer,
        popupProp,
        pane: toString(zIndex),
        customStyleObject
      });
      options = {
        ...styleOptions,
        attribution: layerOps.getAttribution(layer),
        pane: toString(zIndex),
        maxZoom: layerOps.getMaxZoom(layer)
      };
    } else {
      styleOptions = await getGeoJSONStyle({ style, layer, popupProp, customStyleObject });
      options = {
        ...styleOptions,
        attribution: layerOps.getAttribution(layer),
        maxZoom: layerOps.getMaxZoom(layer)
      };
    }
    layerToDraw = L.geoJSON(geojsonFile, options);
    return layerToDraw;
  } catch (error) {
    console.error(error);
    throw new Error(error.message);
  }
};

export default buildGeoJSONLayer;
